import { Router } from "express";
import { db } from "@workspace/db";
import { tendersTable, tenderDocumentsTable } from "@workspace/db";
import { eq, and, ilike, desc, count, sql } from "drizzle-orm";
import multer from "multer";
import path from "path";
import fs from "fs";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { logActivity, logAudit } from "../lib/activity";
import { ensureTenderVectorStore, uploadFileToVectorStore, deleteFileFromVectorStore } from "../lib/vectorStore.js";

const router = Router();

const uploadDir = path.join(process.cwd(), "uploads", "tenders");
fs.mkdirSync(uploadDir, { recursive: true });

const storage = multer.diskStorage({
  destination: uploadDir,
  filename: (_req, file, cb) => {
    const unique = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    cb(null, `${unique}${path.extname(file.originalname)}`);
  },
});
const upload = multer({ storage, limits: { fileSize: 20 * 1024 * 1024 } });

/* zero-downtime migrations */
async function ensureColumns() {
  try {
    await db.execute(sql`ALTER TABLE tender_documents ADD COLUMN IF NOT EXISTS file_path TEXT`);
    await db.execute(sql`ALTER TABLE tender_documents ADD COLUMN IF NOT EXISTS openai_file_id TEXT`);
    await db.execute(sql`ALTER TABLE tender_documents ADD COLUMN IF NOT EXISTS summary TEXT`);
    await db.execute(sql`ALTER TABLE tender_documents ADD COLUMN IF NOT EXISTS ocr_status TEXT`);
    await db.execute(sql`ALTER TABLE tender_documents ADD COLUMN IF NOT EXISTS ocr_error TEXT`);
    await db.execute(sql`ALTER TABLE tenders ADD COLUMN IF NOT EXISTS vector_store_id TEXT`);
  } catch { /* already exists or unsupported */ }
}
ensureColumns();

router.get("/tenders", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const { status, sector, city, search, page = "1", limit = "20" } = req.query as Record<string, string>;
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const pageNum = Math.max(1, parseInt(page));
    const limitNum = Math.min(100, Math.max(1, parseInt(limit)));
    const offset = (pageNum - 1) * limitNum;

    const conditions = [eq(tendersTable.companyId, companyId)];
    if (status) conditions.push(eq(tendersTable.status, status as any));
    if (sector) conditions.push(eq(tendersTable.sector, sector));
    if (city) conditions.push(eq(tendersTable.city, city));
    if (search) conditions.push(ilike(tendersTable.title, `%${search}%`));

    const where = and(...conditions);

    const [items, [total]] = await Promise.all([
      db.select().from(tendersTable).where(where).orderBy(desc(tendersTable.createdAt)).limit(limitNum).offset(offset),
      db.select({ count: count() }).from(tendersTable).where(where),
    ]);

    res.json({ items, total: Number(total?.count ?? 0), page: pageNum, limit: limitNum });
  } catch (err) {
    next(err);
  }
});

router.post("/tenders", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });

    const body = { ...req.body };
    if (body.deadline) {
      const d = new Date(body.deadline);
      body.deadline = isNaN(d.getTime()) ? undefined : d;
    }

    const tender = await db
      .insert(tendersTable)
      .values({ ...body, companyId: req.companyId })
      .returning();

    await logActivity({
      companyId: req.companyId,
      userId: req.dbUser!.id,
      userName: `${req.dbUser!.firstName ?? ""} ${req.dbUser!.lastName ?? ""}`.trim() || req.dbUser!.email,
      action: "created_tender",
      entityType: "tender",
      entityId: tender[0].id,
      entityTitle: tender[0].title,
    });

    res.status(201).json(tender[0]);
  } catch (err) {
    next(err);
  }
});

router.get("/tenders/:id", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tender = await db
      .select()
      .from(tendersTable)
      .where(and(eq(tendersTable.id, Number(req.params.id)), eq(tendersTable.companyId, req.companyId!)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });
    res.json(tender[0]);
  } catch (err) {
    next(err);
  }
});

router.patch("/tenders/:id", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const existing = await db
      .select()
      .from(tendersTable)
      .where(and(eq(tendersTable.id, Number(req.params.id)), eq(tendersTable.companyId, req.companyId!)))
      .limit(1);
    if (!existing[0]) return res.status(404).json({ error: "Tender not found" });

    const patchBody = { ...req.body };
    if (patchBody.deadline) {
      const d = new Date(patchBody.deadline);
      patchBody.deadline = isNaN(d.getTime()) ? undefined : d;
    }

    const updated = await db
      .update(tendersTable)
      .set({ ...patchBody, updatedAt: new Date() })
      .where(eq(tendersTable.id, Number(req.params.id)))
      .returning();

    await logAudit({
      userId: req.dbUser!.id,
      userEmail: req.dbUser!.email,
      companyId: req.companyId,
      tenderId: Number(req.params.id),
      action: "update_tender",
      entityType: "tender",
      entityId: Number(req.params.id),
      before: existing[0] as any,
      after: updated[0] as any,
    });

    res.json(updated[0]);
  } catch (err) {
    next(err);
  }
});

router.delete("/tenders/:id", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    await db
      .delete(tendersTable)
      .where(and(eq(tendersTable.id, Number(req.params.id)), eq(tendersTable.companyId, req.companyId!)));
    res.status(204).send();
  } catch (err) {
    next(err);
  }
});

/* ── PDF upload ── */
router.post("/tenders/:id/upload-pdf", requireAuth, upload.single("file"), async (req: AuthRequest, res, next) => {
  try {
    if (!req.file) return res.status(400).json({ error: "No file uploaded" });

    const tender = await db
      .select()
      .from(tendersTable)
      .where(and(eq(tendersTable.id, Number(req.params.id)), eq(tendersTable.companyId, req.companyId!)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    let extractedText: string | null = null;
    let fileBuffer: Buffer = Buffer.alloc(0);
    try {
      fileBuffer = fs.readFileSync(req.file.path);
      const pdfModule = (await import("pdf-parse")) as any;
      const pdfParse: (buf: Buffer) => Promise<{ text: string }> = pdfModule.default ?? pdfModule;
      const parsed = await pdfParse(fileBuffer);
      extractedText = parsed.text;
    } catch {
      // PDF parsing failed, continue without text
    }

    // Fix multer latin1 mis-interpretation of UTF-8 filenames (Arabic, etc.)
    let originalName = req.file.originalname;
    try {
      const decoded = Buffer.from(originalName, "latin1").toString("utf8");
      if (!decoded.includes("\uFFFD")) originalName = decoded;
    } catch { /* keep as-is */ }

    // Decide whether an OCR fallback should kick in (scanned PDFs).
    const { shouldRunOcrFallback, extractTextFromPdfWithGemini } = await import("../lib/ocr.js");
    const needsOcr = shouldRunOcrFallback(extractedText, req.file.size);
    const initialOcrStatus = needsOcr ? "pending" : "skipped";

    const doc = await db
      .insert(tenderDocumentsTable)
      .values({
        tenderId: Number(req.params.id),
        fileName: originalName,
        fileSize: req.file.size,
        filePath: req.file.path,
        extractedText,
        ocrStatus: initialOcrStatus,
      })
      .returning();

    res.json(doc[0]);

    // Background OCR pipeline — non-blocking. Updates ocr_status as it goes
    // so the frontend can poll /ocr-status while the user keeps working.
    if (needsOcr && fileBuffer.length > 0) {
      const docId = doc[0].id;
      (async () => {
        try {
          await db.execute(sql`UPDATE tender_documents SET ocr_status = 'running' WHERE id = ${docId}`);
          const result = await extractTextFromPdfWithGemini(fileBuffer);
          if (!result) {
            await db.execute(sql`UPDATE tender_documents SET ocr_status = 'failed', ocr_error = ${"GEMINI_API_KEY not configured"} WHERE id = ${docId}`);
            return;
          }
          if (result.text && result.text.length > 50) {
            await db.execute(sql`UPDATE tender_documents SET extracted_text = ${result.text}, ocr_status = 'completed' WHERE id = ${docId}`);
          } else {
            await db.execute(sql`UPDATE tender_documents SET ocr_status = 'failed', ocr_error = ${result.warning || "OCR returned no text"} WHERE id = ${docId}`);
          }
        } catch (err: any) {
          try {
            await db.execute(sql`UPDATE tender_documents SET ocr_status = 'failed', ocr_error = ${String(err?.message ?? err).slice(0, 500)} WHERE id = ${docId}`);
          } catch { /* swallow */ }
        }
      })();
    }

    // Background: upload to OpenAI Vector Store (non-blocking, silent fail)
    const tenderId = Number(req.params.id);
    const docId = doc[0].id;
    const filePath = req.file.path;
    const existingVsId = (tender[0] as any).vectorStoreId ?? null;
    const tenderTitle = tender[0].title;
    (async () => {
      try {
        const vsId = await ensureTenderVectorStore(tenderId, tenderTitle, existingVsId);
        const openaiFileId = await uploadFileToVectorStore(vsId, filePath, originalName);
        await db.execute(sql`UPDATE tenders SET vector_store_id = ${vsId} WHERE id = ${tenderId}`);
        await db.execute(sql`UPDATE tender_documents SET openai_file_id = ${openaiFileId} WHERE id = ${docId}`);
      } catch { /* silent — vector store is enhancement only */ }
    })();
  } catch (err) {
    next(err);
  }
});

/* ── List tender documents ── */
router.get("/tenders/:id/documents", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db
      .select({ id: tendersTable.id })
      .from(tendersTable)
      .where(and(eq(tendersTable.id, Number(req.params.id)), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const docs = await db
      .select()
      .from(tenderDocumentsTable)
      .where(eq(tenderDocumentsTable.tenderId, Number(req.params.id)))
      .orderBy(desc(tenderDocumentsTable.createdAt));
    res.json(docs);
  } catch (err) {
    next(err);
  }
});

/* ── OCR status (poll while a scanned PDF is being OCR'd) ── */
router.get("/tenders/:id/documents/ocr-status", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, Number(req.params.id)), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const rows = await db.execute(sql`
      SELECT id, file_name, ocr_status, ocr_error,
             COALESCE(LENGTH(extracted_text), 0) AS text_len
      FROM tender_documents
      WHERE tender_id = ${Number(req.params.id)}
      ORDER BY created_at DESC
    `);
    res.json({
      documents: (rows.rows as any[]).map((r) => ({
        id: r.id, fileName: r.file_name,
        ocrStatus: r.ocr_status, ocrError: r.ocr_error,
        textLength: Number(r.text_len ?? 0),
      })),
    });
  } catch (err) { next(err); }
});

/* ── Serve a tender document file ── */
router.get("/tenders/:id/documents/:docId/file", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db
      .select({ id: tendersTable.id })
      .from(tendersTable)
      .where(and(eq(tendersTable.id, Number(req.params.id)), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const doc = await db
      .select()
      .from(tenderDocumentsTable)
      .where(and(eq(tenderDocumentsTable.id, Number(req.params.docId)), eq(tenderDocumentsTable.tenderId, Number(req.params.id))))
      .limit(1);
    if (!doc[0]) return res.status(404).json({ error: "Document not found" });

    const filePath = (doc[0] as any).filePath;
    if (!filePath || !fs.existsSync(filePath)) {
      return res.status(404).json({ error: "File not found on disk" });
    }

    res.sendFile(path.resolve(filePath));
  } catch (err) {
    next(err);
  }
});

/* ── Delete a tender document ── */
router.delete("/tenders/:id/documents/:docId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db
      .select({ id: tendersTable.id })
      .from(tendersTable)
      .where(and(eq(tendersTable.id, Number(req.params.id)), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const doc = await db
      .select()
      .from(tenderDocumentsTable)
      .where(and(eq(tenderDocumentsTable.id, Number(req.params.docId)), eq(tenderDocumentsTable.tenderId, Number(req.params.id))))
      .limit(1);
    if (!doc[0]) return res.status(404).json({ error: "Document not found" });

    // Delete file from disk if it exists
    const filePath = (doc[0] as any).filePath;
    if (filePath && fs.existsSync(filePath)) {
      try { fs.unlinkSync(filePath); } catch { /* ignore */ }
    }

    await db
      .delete(tenderDocumentsTable)
      .where(eq(tenderDocumentsTable.id, Number(req.params.docId)));

    res.status(204).send();

    // Background: remove from OpenAI Vector Store
    const openaiFileId = doc[0].openaiFileId;
    const vsRow = await db.select({ vsId: tendersTable.vectorStoreId }).from(tendersTable)
      .where(eq(tendersTable.id, Number(req.params.id))).limit(1).catch(() => []);
    const vsId = vsRow[0]?.vsId;
    if (vsId && openaiFileId) {
      (async () => {
        try { await deleteFileFromVectorStore(vsId, openaiFileId); } catch { }
      })();
    }
  } catch (err) {
    next(err);
  }
});

export default router;
